// Widget editor, create or update widgets

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createWidget, updateWidget, getWidgetById, uploadWidgetImage } from '../../services/widgetService';

const WidgetEditor = () => {
  const DEVICE_OPTIONS = ['web', 'ios', 'android', 'others'];
  const TAG_OPTIONS = ['analysis', 'play', 'learn', 'openings', 'people', 'books', 'news', 'watch', 'community', 'podcasts'];

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    icon: '',
    link: '',
    indie: false,
    hidden: false,
    tagInput: '',
    devicesInput: '',
    devices: [],
    tagMulti: [],
    verified: false,
    isNew: false,
    isActive: false,
    date: '',
    description: '',
    comments: '',
    excludeFromAll: false,
    slug: '',
    longDescription: '',
    featuresInput: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const bodyTextareaRef = useRef(null);
  const [linksEntries, setLinksEntries] = useState([{ key: '', value: '' }]);

  useEffect(() => {
    if (isEditing) {
      fetchWidget();
    }
  }, [id, isEditing]);

  const fetchWidget = async () => {
    try {
      setLoading(true);
      const widget = await getWidgetById(id);
      
      if (!widget) {
        setError('Widget not found');
        return;
      }
      
      setFormData({
        title: widget.title || '',
        subtitle: widget.subtitle || '',
        icon: widget.icon || '',
        link: widget.link || '',
        indie: Boolean(widget.indie) || false,
        hidden: Boolean(widget.hidden) || false,
        tagInput: Array.isArray(widget.tag) ? widget.tag.join(', ') : (widget.tag || ''),
        tagMulti: Array.isArray(widget.tag) ? widget.tag : (widget.tag ? [widget.tag] : []),
        devicesInput: Array.isArray(widget.devices) ? widget.devices.join(', ') : '',
        devices: Array.isArray(widget.devices) ? widget.devices : [],
        verified: Boolean(widget.verified) || false,
        isNew: Boolean(widget.isNew) || false,
        isActive: Boolean(widget.isActive) || false,
        date: widget.date || '',
        description: widget.description || '',
        comments: widget.comments || '',
        excludeFromAll: Boolean(widget.excludeFromAll) || false,
        slug: widget.slug || '',
        longDescription: widget.longDescription || '',
        featuresInput: Array.isArray(widget.features) ? widget.features.join('\n') : ''
      });

      if (widget.links && typeof widget.links === 'object') {
        const newEntries = Object.entries(widget.links).map(([key, val]) => ({
          key,
          value: Array.isArray(val) ? val.join(', ') : String(val)
        }));
        setLinksEntries(newEntries.length ? newEntries : [{ key: '', value: '' }]);
      } else {
        setLinksEntries([{ key: '', value: '' }]);
      }
    } catch (err) {
      console.error('Error fetching widget:', err);
      setError('Failed to load widget');
    } finally {
      setLoading(false);
    }
  };
  
  // Links editor helpers
  const addLinkRow = () => {
    setLinksEntries(prev => [...prev, { key: '', value: '' }]);
  };

  const removeLinkRow = (index) => {
    setLinksEntries(prev => prev.filter((_, i) => i !== index));
  };

  const updateLinkRow = (index, field, newValue) => {
    setLinksEntries(prev => prev.map((row, i) => i === index ? { ...row, [field]: newValue } : row));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImageUploading(true);
      const imageUrl = await uploadWidgetImage(file);
      setFormData(prev => ({
        ...prev,
        icon: imageUrl
      }));
      setImageFile(null);
    } catch (err) {
      console.error('Error uploading image:', err);
      alert(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.icon.trim()) {
      setError('Icon is required');
      return;
    }
    
    if (!formData.link.trim()) {
      setError('Link is required');
      return;
    }

    // Validate standard date format if provided
    if (formData.date && Number.isNaN(Date.parse(formData.date))) {
      setError('Please provide a valid date (YYYY-MM-DD).');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const parseCommaList = (text) => text
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const linksObj = linksEntries.reduce((acc, { key, value }) => {
        const k = (key || '').trim();
        const v = (value || '').trim();
        if (!k || !v) return acc;
        // Only single URL per key
        acc[k] = v;
        return acc;
      }, {});

      const tagValue = (() => {
        // Prefer multi-select; fall back to text input if multi empty
        if (formData.tagMulti && formData.tagMulti.length) return formData.tagMulti;
        const raw = (formData.tagInput || '').trim();
        if (!raw) return "";
        const parts = parseCommaList(raw);
        return parts.length <= 1 ? raw : parts;
      })();

      const widgetData = {
        title: formData.title.trim(),
        subtitle: formData.subtitle?.trim() || "",
        icon: formData.icon.trim(),
        link: formData.link.trim(),
        indie: !!formData.indie,
        hidden: !!formData.hidden,
        tag: tagValue,
        devices: (formData.devices && formData.devices.length)
          ? formData.devices
          : (formData.devicesInput ? parseCommaList(formData.devicesInput) : ""),
        verified: !!formData.verified,
        links: Object.keys(linksObj).length ? linksObj : "",
        isNew: !!formData.isNew,
        isActive: !!formData.isActive,
        date: formData.date?.trim() || "",
        description: formData.description?.trim() || "",
        comments: formData.comments?.trim() || "",
        excludeFromAll: !!formData.excludeFromAll,
        slug: formData.slug?.trim() || "",
        longDescription: formData.longDescription?.trim() || "",
        features: formData.featuresInput
          ? formData.featuresInput.split('\n').map(f => f.trim()).filter(Boolean)
          : []
      };

      if (isEditing) {
        await updateWidget(id, widgetData);
      } else {
        await createWidget(widgetData);
      }
      
      navigate('/admin/widgets');
    } catch (err) {
      console.error('Error saving widget:', err);
      setError(err.message || 'Failed to save widget');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/admin/widgets');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading widget...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-100">
            {isEditing ? 'Edit Widget' : 'Create New Widget'}
          </h2>
          <p className="text-gray-400 mt-2">
            {isEditing ? 'Update your widget' : 'Share insights with the chess community'}
          </p>
        </div>
        <button
          onClick={handleCancel}
          className="text-gray-400 hover:text-gray-300 px-4 py-2 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Title */}
          <div className="md:col-span-2">
            <label htmlFor="title" className="block text-gray-300 mb-2 font-medium">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Enter widget title"
              required
            />
          </div>

          {/* Subtitle */}
          <div>
            <label htmlFor="subtitle" className="block text-gray-300 mb-2 font-medium">
              Subtitle
            </label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Max 3 words"
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-gray-300 mb-2 font-medium">
              Slug
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. lichess"
            />
            {formData.slug && (
              <p className="text-gray-500 text-sm mt-1">trendingchess.com/{formData.slug}</p>
            )}
          </div>
        </div>

        {/* Link (required) and Visibility/Indie */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="link" className="block text-gray-300 mb-2 font-medium">Main Link *</label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="https://example.com or /internal-route"
              required
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="indie"
              name="indie"
              checked={formData.indie}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="indie" className="text-gray-300 font-medium">Indie</label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="hidden"
              name="hidden"
              checked={formData.hidden}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="hidden" className="text-gray-300 font-medium">Hidden</label>
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">
            Icon *
          </label>
          
          <div className="space-y-4">
            {/* Current Image Preview */}
            {formData.icon && (
              <div className="relative">
                <img 
                  src={formData.icon} 
                  alt="Icon"
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors"
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Image Upload */}
            <div className="flex gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={imageUploading}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                  imageUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {imageUploading ? 'Uploading...' : 'Upload Image'}
              </label>
              
              {/* Manual URL Input */}
              <input
                type="url"
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Or paste image URL"
              />
            </div>
          </div>
        </div>

        {/* Tags, Devices */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Tag(s)</label>
            <div className="flex flex-wrap gap-3 bg-gray-700 border border-gray-600 rounded-lg p-3">
              {TAG_OPTIONS.map(opt => (
                <label key={opt} className="flex items-center gap-2 text-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.tagMulti.includes(opt)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        tagMulti: checked
                          ? Array.from(new Set([...(prev.tagMulti||[]), opt]))
                          : (prev.tagMulti||[]).filter(v => v !== opt)
                      }));
                    }}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  {opt}
                </label>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-1">Select all that apply.</p>
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Devices</label>
            <div className="flex flex-wrap gap-3 bg-gray-700 border border-gray-600 rounded-lg p-3">
              {DEVICE_OPTIONS.map(opt => (
                <label key={opt} className="flex items-center gap-2 text-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.devices.includes(opt)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        devices: checked
                          ? Array.from(new Set([...(prev.devices||[]), opt]))
                          : (prev.devices||[]).filter(v => v !== opt)
                      }));
                    }}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  {opt}
                </label>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-1">Select all that apply.</p>
          </div>
        </div>

        {/* Status Toggles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="verified"
              name="verified"
              checked={formData.verified}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="verified" className="text-gray-300 font-medium">Verified</label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isNew"
              name="isNew"
              checked={formData.isNew}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="isNew" className="text-gray-300 font-medium">New</label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="text-gray-300 font-medium">Active</label>
          </div>
        </div>

        {/* Links editor */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="block text-gray-300 font-medium">Links</label>
            <button type="button" onClick={addLinkRow} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded">+ Add Link</button>
          </div>
          {linksEntries.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-3 items-center">
              <input
                type="text"
                value={row.key}
                onChange={(e) => updateLinkRow(idx, 'key', e.target.value)}
                className="col-span-3 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Link name"
              />
              <input
                type="url"
                value={row.value}
                onChange={(e) => updateLinkRow(idx, 'value', e.target.value)}
                className="col-span-8 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="https://example.com"
              />
              <button type="button" onClick={() => removeLinkRow(idx)} className="col-span-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded">×</button>
            </div>
          ))}
        </div>

        {/* Date */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Description</label>
          <textarea
            rows="4"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            placeholder="Extended description"
          />
        </div>

        {/* Long Description (HTML) */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Long Description (HTML)</label>
          <textarea
            rows="8"
            value={formData.longDescription}
            onChange={(e) => setFormData(prev => ({ ...prev, longDescription: e.target.value }))}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono text-sm"
            placeholder="<p>Rich description for the landing page...</p>"
          />
          <p className="text-gray-500 text-sm mt-1">Displayed on the detail page at /{formData.slug || 'slug'}. Supports HTML.</p>
        </div>

        {/* Features */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Features (one per line)</label>
          <textarea
            rows="5"
            value={formData.featuresInput}
            onChange={(e) => setFormData(prev => ({ ...prev, featuresInput: e.target.value }))}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            placeholder={"Free and open source\nPuzzle training\nOnline tournaments\nGame analysis"}
          />
          <p className="text-gray-500 text-sm mt-1">{formData.featuresInput ? formData.featuresInput.split('\n').filter(f => f.trim()).length : 0} features</p>
        </div>

        {/* Comments */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Comments (internal)</label>
          <textarea
            rows="3"
            value={formData.comments}
            onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            placeholder="Internal notes"
          />
        </div>

        {/* Exclude From All */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="excludeFromAll"
            name="excludeFromAll"
            checked={formData.excludeFromAll}
            onChange={handleInputChange}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
          />
          <label htmlFor="excludeFromAll" className="text-gray-300 font-medium">Exclude from all</label>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-6 border-t border-gray-700">
          <button
            type="submit"
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg transition-colors font-medium"
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Widget' : 'Create Widget')}
          </button>
          
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default WidgetEditor;